import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Crown, Upload } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import { PremiumBadge } from './PremiumBadge';
import { PremiumUpgradeModal } from './PremiumUpgradeModal';
import { PhotoUploadModal } from './PhotoUploadModal';
import { useFavorites } from '../contexts/FavoritesContext';

export function ProfileHeader({ user, isPremiumUser = false }) {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const { favorites } = useFavorites();

  const getInitials = (email) => {
    if (!email) return 'U';
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-2 gap-12 mb-16"
      >
        {/* Left: Profile info */}
        <div className="flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-full bg-stone-200 flex items-center justify-center overflow-hidden">
                <span className="text-lg text-stone-600">{getInitials(user?.email)}</span>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h2 className="serif text-4xl">My Profile</h2>
                  {isPremiumUser && <PremiumBadge size="sm" showLabel={false} />}
                </div>
                <p className="text-sm text-muted-foreground">{user?.email || 'Not logged in'}</p>
              </div>
            </div>

            {/* Photo Upload Button */}
            <button
              onClick={() => setShowUploadModal(true)}
              className="w-full h-12 border border-foreground hover:bg-foreground hover:text-background transition-all text-xs uppercase tracking-widest mb-6 flex items-center justify-center gap-2"
            >
              <Upload className="w-4 h-4" />
              <span>Upload Photo</span>
            </button>

            {/* Premium Upgrade Banner */}
            {!isPremiumUser && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                className="bg-gradient-to-r from-amber-50 to-stone-100 border border-amber-200 p-6 mb-8 cursor-pointer group"
                onClick={() => setShowPremiumModal(true)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-amber-100 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-amber-700" fill="currentColor" strokeWidth={0} />
                    </div>
                    <div>
                      <h3 className="serif text-xl mb-1">Upgrade to Premium</h3>
                      <p className="text-xs text-muted-foreground">
                        Ad-free experience, early reservations & exclusive events
                      </p>
                    </div>
                  </div>
                  <div className="px-6 py-2 border border-foreground group-hover:bg-foreground group-hover:text-background transition-all text-xs uppercase tracking-widest">
                    Learn More
                  </div>
                </div>
              </motion.div>
            )}

            <div className="h-px bg-border mb-8"></div>

            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-8">
              <div>
                <div className="serif text-5xl mb-2">0</div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Dishes Rated</p>
              </div>
              <div>
                <div className="serif text-5xl mb-2">{favorites.length}</div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Favorites</p>
              </div>
              <div>
                <div className="serif text-5xl mb-2">0</div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Following</p>
              </div>
            </div>

            <div className="h-px bg-border my-8"></div>

            <div className="space-y-3">
              <p className="text-sm text-muted-foreground italic">
                "Good food is the foundation of genuine happiness."
              </p>
              <p className="text-xs text-muted-foreground">Member since {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>
          </motion.div>
        </div>

        {/* Right: Featured image */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="relative aspect-[4/5] overflow-hidden"
        >
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1763867641117-0c1f154ee9a4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxnb3VybWV0JTIwcGxhdGVkJTIwZGlzaCUyMHJlc3RhdXJhbnR8ZW58MXx8fHwxNzY0MDk2NTgyfDA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="Featured dish"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-8">
            <p className="text-white text-sm mb-1">Featured Collection</p>
            <h3 className="serif text-white text-3xl">Your Culinary Journey</h3>
          </div>
        </motion.div>
      </motion.div>

      {/* Premium Upgrade Modal */}
      <AnimatePresence>
        {showPremiumModal && (
          <PremiumUpgradeModal
            onClose={() => setShowPremiumModal(false)}
            user={user}
          />
        )}
      </AnimatePresence>

      {/* Photo Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <PhotoUploadModal
            user={user}
            onClose={() => setShowUploadModal(false)}
            onSuccess={(photoURL) => {
              // Photo uploaded successfully
              setShowUploadModal(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

